/**
 * `/settings/sign-in-methods`, the profile-settings sign-in-methods screen
 * (design prompt 44.1 to 44.4). The file is named
 * `settings_.sign-in-methods` so this route stays out of `/settings`'s tree,
 * which has no `Outlet`. The remove confirm is a module-bus promise, not URL
 * state. The email wizard step IS URL state because a fan can refresh into
 * it. An Apple or Google row is drawn only when that provider is linked or
 * usable in this browser, and every drawn row states Connected or Not
 * connected (ISSUE-1177).
 */
import {
  useEmailRemove,
  useIdentityMethodsGet,
  useIdentityUnlink,
} from "@sportsmet/auth-sdk/private";
import { useOauthProviderConfigGet } from "@sportsmet/auth-sdk/public";
import { Button, Card, confirmDestructive } from "@sportsmet/ui";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../errors/apiErrorBus";
import { SettingsBackLink } from "../features/account/SettingsBackLink";
import { EmailChangeForm } from "../features/signInMethods/EmailChangeForm";
import { LinkProviderPanel } from "../features/signInMethods/LinkProviderPanel";
import { ProviderConflictDialog } from "../features/signInMethods/ProviderConflictDialog";
import {
  ADD_LABEL,
  CHANGE_LABEL,
  isMethodLinked,
  isOnlyRemainingMethod,
  KEEP_ONE_METHOD_LINE,
  methodRowLabel,
  methodRowValue,
  REMOVE_LABEL,
  removeConfirmBody,
  removeConfirmTitle,
  shouldShowContextLine,
  SIGN_IN_METHODS_BACK_LABEL,
  SIGN_IN_METHODS_CONTEXT_LINE,
  SIGN_IN_METHODS_HEADING,
  type typeSignInMethodKey,
  type typeSignInMethodsData,
  visibleSignInMethods,
} from "../features/signInMethods/signInMethodsScreenLogic";
import { coerceEnum, coerceOptionalString } from "../routeState/searchParams";
import { BACK_LINK_CLASS } from "../shell/BackLink";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";
import { SectionScreen } from "../shell/SectionScreen";

/**
 * A usable id is a non-empty string. Null, undefined, and "" all mean the
 * operator has not configured this provider, so there is nothing to hand the
 * provider's JavaScript and nothing to draw. Copied from
 * `ProviderSignInPanel.tsx` rather than exported from it: that directory is
 * sign-in-critical and out of scope for this plan.
 */
function isUsableId(candidate: string | null | undefined): candidate is string {
  return typeof candidate === "string" && candidate !== "";
}

export const SIGN_IN_METHODS_STEPS = ["methods", "add-email", "code"] as const;
export type typeSignInMethodsStep = (typeof SIGN_IN_METHODS_STEPS)[number];
/**
 * `step` is typed optional here, unlike `coerceEnum`'s always-concrete
 * return, to match `sectionSearch.ts`'s convention for every other route's
 * search type in this app: TanStack Router requires a Link's `search` prop
 * whenever ANY field of the target route's search type is non-optional, so a
 * required `step` here would force `search={{ step: "methods" }}` onto every
 * plain `<Link to="/settings/sign-in-methods">` and put `?step=methods` on
 * an href that should read clean. `validateSearch` below still always
 * returns a concrete value; only the type is widened.
 */
export type typeSignInMethodsSearch = {
  step?: typeSignInMethodsStep | undefined;
  requestId?: string | undefined;
};

function validateSearch(
  search: Record<string, unknown>,
): typeSignInMethodsSearch {
  return {
    step: coerceEnum(search.step, SIGN_IN_METHODS_STEPS, "methods"),
    requestId: coerceOptionalString(search.requestId),
  };
}

const SignInMethodsScreen = (): ReactNode => {
  const search = useSearch({ from: "/settings_/sign-in-methods" });
  const navigate = useNavigate({ from: "/settings/sign-in-methods" });
  const [conflictProvider, setConflictProvider] = useState<
    "apple" | "google" | undefined
  >(undefined);
  const methods = useIdentityMethodsGet({});
  const unlink = useIdentityUnlink();
  const removeEmail = useEmailRemove();
  const data: typeSignInMethodsData | null = methods.data?.data ?? null;

  const providerConfig = useOauthProviderConfigGet({});
  const googleConfig = providerConfig.data?.data?.google;
  const appleConfig = providerConfig.data?.data?.apple;
  const appleWebServicesId =
    appleConfig?.isEnabled === true && isUsableId(appleConfig.webServicesId)
      ? appleConfig.webServicesId
      : null;
  const googleWebClientId =
    googleConfig?.isEnabled === true && isUsableId(googleConfig.webClientId)
      ? googleConfig.webClientId
      : null;

  useEffect(() => {
    const code = extractErrorCode(methods.data);
    if (code !== null)
      showApiError(methods.data, undefined, methods.dataUpdatedAt);
  }, [methods.dataUpdatedAt]);

  if (methods.isPending || providerConfig.isPending)
    return <RoutePendingScreen />;

  // `search.step` is optional in the type only to keep a bare `<Link>` to
  // this route valid (see `typeSignInMethodsSearch`'s comment above);
  // `validateSearch` always resolves a concrete value, so this default is
  // never actually reached at runtime.
  const step = search.step ?? "methods";

  if (step !== "methods") {
    return (
      <SectionScreen
        heading={SIGN_IN_METHODS_HEADING}
        back={
          <Link
            to="/settings/sign-in-methods"
            search={{ step: "methods" }}
            className={BACK_LINK_CLASS}
            data-testid="sign-in-methods-back-to-list"
          >
            {SIGN_IN_METHODS_BACK_LABEL}
          </Link>
        }
      >
        <Card>
          <EmailChangeForm
            step={step}
            requestId={search.requestId}
            onRequested={({ requestId }) => {
              void navigate({ search: { step: "code", requestId } });
            }}
            onConfirmed={() => {
              void navigate({ search: { step: "methods" } });
            }}
            onCancel={() => {
              void navigate({ search: { step: "methods" } });
            }}
          />
        </Card>
      </SectionScreen>
    );
  }

  const visibleMethods = visibleSignInMethods(data, {
    isAppleAvailable: appleWebServicesId !== null,
    isGoogleAvailable: googleWebClientId !== null,
  });
  const showContextLine = shouldShowContextLine(visibleMethods);

  function isRemoving(method: typeSignInMethodKey): boolean {
    return method === "email"
      ? removeEmail.isPending
      : unlink.isPending && unlink.variables.provider === method;
  }

  async function handleRemove(method: typeSignInMethodKey): Promise<void> {
    const confirmed = await confirmDestructive({
      title: removeConfirmTitle(method),
      body: removeConfirmBody(data, method),
      confirmLabel: REMOVE_LABEL,
    });
    if (!confirmed) return;
    try {
      const result =
        method === "email"
          ? await removeEmail.mutateAsync({})
          : await unlink.mutateAsync({ provider: method });
      showApiError(result, "signInMethodRemove");
    } catch {
      showOfflineError();
    }
  }

  return (
    <SectionScreen
      heading={SIGN_IN_METHODS_HEADING}
      back={<SettingsBackLink />}
    >
      <Card>
        <div className="flex flex-col gap-3">
          {visibleMethods.map((method) => {
            const linked = isMethodLinked(data, method);
            const value = methodRowValue(data, method);

            return (
              <div
                key={method}
                className="flex items-center justify-between gap-3"
                data-testid={`sign-in-method-row-${method}`}
              >
                <div className="flex flex-col">
                  <span className="text-body text-ink">
                    {methodRowLabel(method)}
                  </span>
                  <span
                    className="text-caption text-ink-secondary"
                    data-testid={`sign-in-method-value-${method}`}
                  >
                    {value}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {isOnlyRemainingMethod(data, method) ? (
                    <span
                      className="text-caption text-ink-secondary"
                      data-testid={`sign-in-method-keep-one-${method}`}
                    >
                      {KEEP_ONE_METHOD_LINE}
                    </span>
                  ) : linked ? (
                    <>
                      {method === "email" ? (
                        <Button
                          variant="link"
                          size="sm"
                          data-testid="sign-in-method-change-email"
                          onClick={() => {
                            void navigate({ search: { step: "add-email" } });
                          }}
                        >
                          {CHANGE_LABEL}
                        </Button>
                      ) : null}
                      <Button
                        variant="destructive"
                        size="sm"
                        data-testid={`sign-in-method-remove-${method}`}
                        loading={isRemoving(method)}
                        loadingLabel={`Removing ${methodRowLabel(method)}`}
                        onClick={() => {
                          void handleRemove(method);
                        }}
                      >
                        {REMOVE_LABEL}
                      </Button>
                    </>
                  ) : method === "email" ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="sign-in-method-add-email"
                      onClick={() => {
                        void navigate({ search: { step: "add-email" } });
                      }}
                    >
                      {ADD_LABEL}
                    </Button>
                  ) : method === "apple" ? (
                    <LinkProviderPanel
                      provider="apple"
                      webServicesId={appleWebServicesId}
                      webClientId={null}
                      onConflict={() => {
                        setConflictProvider("apple");
                      }}
                    />
                  ) : (
                    <LinkProviderPanel
                      provider="google"
                      webServicesId={null}
                      webClientId={googleWebClientId}
                      onConflict={() => {
                        setConflictProvider("google");
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {showContextLine ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="sign-in-methods-context"
            >
              {SIGN_IN_METHODS_CONTEXT_LINE}
            </p>
          ) : null}
        </div>
      </Card>
      <ProviderConflictDialog
        provider={conflictProvider}
        onClose={() => {
          setConflictProvider(undefined);
        }}
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/settings_/sign-in-methods")({
  beforeLoad: requireSignedIn,
  validateSearch,
  component: SignInMethodsScreen,
});
